import { UtilityFunctions } from './../../../classes/UtilityFunctions';

import { Component, OnInit, OnDestroy, Input, ElementRef } from '@angular/core';
import { Params } from '@angular/router';

import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';

import { DisplayStateService, Events } from '../../../services/DisplayStateService';
import { AppStateService } from '../../../services/AppStateService';
import { WebPlatformApiService } from '../../../services/WebPlatformApiService';
import { DataRequestReply } from '../../../interfaces/DisplayInterfaces';
import { WidgetConfiguration } from '../../../interfaces/WidgetInterfaces';
import { DataRequestError } from '../../../interfaces/DisplayInterfaces';
import { ClientSettingsService } from '../../../services/ClientSettingsService';

@Component({
  selector: 'app-base-widget-component',
  template: `<p>
  This is an 'abstract' widget and should not be displayed.
</p>
`,
  styles: [``]
})
// tslint:disable-next-line:component-class-suffix
export class BaseWidget implements OnInit, OnDestroy {

  @Input() Config: WidgetConfiguration;
  @Input() heightSpec = '';

  protected ngUnsubscribe: Subject<boolean> = new Subject();
  protected statechanged$: Subscription;
  protected messageHouseListener$: Subscription;
  protected dataAvailableListener$: Subscription;
  protected dataErrorListener$: Subscription;
  public minimumVersionProblem: string;

constructor(
  protected element: ElementRef,
  protected clientSettingsService: ClientSettingsService,
  protected displayStateService: DisplayStateService,
  protected appStateService: AppStateService,
  protected webPlatformApiService: WebPlatformApiService) {}

  ngOnInit() {

    if (this.minimumApiVersion()) {
      const results = UtilityFunctions.checkApiVersion(this.minimumApiVersion(), this.displayStateService);
      if (results.error) {
        this.minimumVersionProblem = results.errorMessage;
      } else if (!results.pass) {
        this.minimumVersionProblem = results.message;
      }      
    }
    
    // Listen for new data available and call a method the child class can override to act on the event
    this.dataAvailableListener$ = this.displayStateService.DataAvailable
      .takeUntil(this.ngUnsubscribe)
      .subscribe(reply => {
        this.onDataAvailable(reply);
      });

    this.dataErrorListener$ = this.displayStateService.DataError
      .takeUntil(this.ngUnsubscribe)
      .subscribe(error => {
        this.onDataError(error);
      });

    // Listen for changes to context, filters, or sorts and call methods the child class can override to act on the event
    this.statechanged$ = this.displayStateService.AppStateChanged
      .takeUntil(this.ngUnsubscribe)
      .subscribe(x => {
        if (x === Events.CURRENT_CONTEXT_CHANGED) {
          this.onContextChanged(this.displayStateService.appState.current.context);
        } else if (x === Events.CURRENT_FILTERS_CHANGED) {
          this.onFiltersChanged(this.displayStateService.appState.current.filters);
        } else if (x === Events.CURRENT_SORTS_CHANGED) {
          this.onSortsChanged(this.displayStateService.appState.current.sorts);
        }
        this.onStateChanged(x);
      });

    // Listen for new messages from other widgets and call a method the child class can override to act on the event
    this.messageHouseListener$ = this.displayStateService.MessageHouse
      .takeUntil(this.ngUnsubscribe)
      .subscribe(messageParams => {
        if (messageParams['deselected'] && this.Config &&  this.Config.name === messageParams['deselected']) {
          this.onTabDeactivated();
        } else if (messageParams['selected'] && this.Config && this.Config.name === messageParams['selected']) {
          this.onTabActivated();
        }
        this.onNewMessage(messageParams);
      });

    // call a method the child class can override if they want to do anything at initialization.
    this.onInitialize();
  }

  minimumApiVersion(): number[] { return undefined;}

  onDataAvailable(reply: DataRequestReply) { }

  onDataError(error: DataRequestError) { }

  onInitialize() { }

  onNewMessage(messageParams: Params) { }

  onTabDeactivated() { }

  onTabActivated() { }

  onContextChanged(currentContext: Params) { }

  onFiltersChanged(currentFilters: Params) { }

  onSortsChanged(currentSorts: Params) { }

  onStateChanged(eventName: string) { }

  ngOnDestroy() {
    this.ngUnsubscribe.next(true);
    this.ngUnsubscribe.complete();
  }
}
